import axios from "../axios";
import toast from "react-hot-toast";

// Subscription type is now a string to support custom values
// Common predefined values: "monthly", "quarterly", "half_yearly", "yearly", "pilot", "one-off"
export type SubscriptionType = string;

export type Company = {
  company_id: number;
  company_name: string;
  company_logo?: string | null;
  subscription_name?: string | null;
  subscription_type?: SubscriptionType | null;
  is_archived?: boolean;
  is_pinned?: boolean;
  quick_runs_quota?: number;
  comprehensive_runs_quota?: number;
  quick_run_duration?: number;
  comprehensive_run_duration?: number;
  industry?: string | null;
  contact_person_name?: string | null;
  contact_email?: string | null;
  contact_phone?: string | null;
  company_size?: string | null;
  company_address?: string | null;
  users?: Array<{
    user_id: number;
    username: string;
    email: string;
    role: number;
    parent_user_id?: number | null;
    is_approved?: boolean;
    created_at?: string;
  }>;
  created_at?: string;
  updated_at?: string;
  subscription_start_date?: string | null;
  subscription_end_date?: string | null;
};

// ---------------------------------------------------------------------------
// Asset Groups & Assets (Inventory) types
// ---------------------------------------------------------------------------

// Legacy types (deprecated - will be removed in future version)
export type AssetGroupStatus = "ACTIVE" | "INACTIVE" | "DEPRECATED";

export type AssetGroup = {
  asset_group_id: number;
  company_id: number | null;
  name: string;
  description?: string | null;
  status?: AssetGroupStatus;
  business_owner_id?: number | null;
  technical_owner_id?: number | null;
  created_by?: number | null;
  metadata?: Record<string, unknown> | null;
  created_at?: string;
  updated_at?: string;
  assetCount?: number; // Count of assets under this asset group
};

// Legacy asset types (deprecated)
export type LegacyAssetType =
  | "WEB_APP"
  | "MOBILE_APP"
  | "API"
  | "CLOUD"
  | "NETWORK"
  | "SOURCE_CODE"
  | "IOS"
  | "ANDROID"
  | "DATABASE"
  | "WEB3"
  | "SMART_CONTRACT"
  | "PROTOCOL"
  | "OTHER";

// ---------------------------------------------------------------------------
// NEW CTB Asset Inventory Framework Types
// ---------------------------------------------------------------------------

/**
 * Asset Type - Business system categorization
 * Based on CTB Asset Inventory Framework
 */
export type AssetType =
  | "WEB_APPLICATION"
  | "API"
  | "MOBILE_APPLICATION"
  | "NETWORK_INFRASTRUCTURE"
  | "CLOUD_RESOURCE"
  | "REPOSITORY_CODE"
  | "IDENTITY_IAM"
  | "INTEGRATION";

/**
 * Asset Status - Lifecycle state
 */
export type AssetStatus = "Discovered" | "Verified" | "Active" | "Archived" | "Zombie";

/**
 * Asset Criticality - Business impact if compromised/unavailable
 */
export type AssetCriticality = "Low" | "Medium" | "High" | "Critical";

/**
 * Data Sensitivity - Classification of data handled by the asset
 */
export type DataSensitivity = "Public" | "Internal" | "Confidential" | "Restricted";

/**
 * Target Type - Type of technical address
 */
export type TargetType = "URL" | "DOMAIN" | "IP" | "CIDR" | "CLOUD";

/**
 * Target Environment - Deployment context
 */
export type TargetEnvironment = "Production" | "UAT" | "Staging" | "Development";

/**
 * Target Validation State
 */
export type TargetValidationState = "PENDING" | "VALID" | "WARNING" | "INVALID";

/**
 * Target Verification State
 */
export type TargetVerificationState = "UNVERIFIED" | "VERIFIED" | "FAILED";

/**
 * Discovery Method - How the target was added to the inventory
 */
export type DiscoveryMethod = "MANUAL" | "IMPORT" | "PENTESTER" | "AI_DISCOVERY";

/**
 * Asset Target - Technical address under an asset
 */
export type AssetTarget = {
  target_id: number;
  asset_id: number;
  target_type: TargetType;
  target_value: string;
  environment: TargetEnvironment;
  validation_state: TargetValidationState;
  verification_state: TargetVerificationState;
  discovery_method: DiscoveryMethod;
  notes?: string | null;
  tech_stack?: string[] | null;
  metadata?: Record<string, unknown> | null;
  created_by?: number | null;
  created_at?: string;
  updated_at?: string;
  deleted_at?: string | null;
};

/**
 * Resource Type - Categorizes the type of resource under a target
 */
export type ResourceType = "PATH" | "PARAMETER" | "HEADER" | "IP" | "PORT" | "FILE" | "OTHER";

/**
 * Resource Status - Testing/scope status of the resource
 */
export type ResourceStatus = "IN_SCOPE" | "OUT_OF_SCOPE" | "TESTED" | "PENDING" | "EXCLUDED";

/**
 * Target Resource - Child resource under an asset target
 * Examples: URL paths, query parameters, individual IPs from CIDR, ports
 */
export type TargetResource = {
  resource_id: number;
  target_id: number;
  value: string;
  resource_type: ResourceType;
  status: ResourceStatus;
  discovery_method: DiscoveryMethod;
  http_method?: string | null;
  notes?: string | null;
  metadata?: Record<string, unknown> | null;
  created_by?: number | null;
  created_at?: string;
  updated_at?: string;
  deleted_at?: string | null;
};

/**
 * Create Target Resource Request
 */
export interface CreateTargetResourceRequest {
  target_id: number;
  value: string;
  resource_type: ResourceType;
  status?: ResourceStatus;
  http_method?: string;
  notes?: string;
  metadata?: Record<string, unknown>;
}

/**
 * Update Target Resource Request
 */
export interface UpdateTargetResourceRequest {
  value?: string;
  resource_type?: ResourceType;
  status?: ResourceStatus;
  http_method?: string;
  notes?: string;
  metadata?: Record<string, unknown>;
}

/**
 * Global Context File Type - Classification of uploaded files
 */
export type GlobalContextFileType =
  | "diagram"
  | "walkthrough"
  | "document"
  | "video"
  | "image"
  | "other";

/**
 * Global Context File - Files attached to asset context
 */
export interface GlobalContextFile {
  file_id: string;
  file_name: string;
  file_type: GlobalContextFileType;
  mime_type: string;
  file_url: string;
  file_size: number;
  description?: string;
  uploaded_at: string;
  uploaded_by?: number;
}

/**
 * Asset Global Context - Product overview and supporting files
 */
export interface AssetGlobalContext {
  product_overview?: string;
  files?: GlobalContextFile[];
}

/**
 * Asset - Business system in the inventory
 * Per CTB Asset Inventory Framework
 */
export type Asset = {
  asset_id: number;
  company_id: number;
  name: string;
  description?: string | null;
  type: AssetType;
  status: AssetStatus;
  criticality: AssetCriticality;
  owner_label?: string | null;
  data_sensitivity?: DataSensitivity | null;
  business_owner_id?: number | null;
  technical_owner_id?: number | null;
  created_by?: number | null;
  metadata?: Record<string, unknown> | null;
  tags?: string[] | null;
  global_context?: AssetGlobalContext | null;
  auth_required?: boolean;
  access_surface_type?: string | null;
  role_context?: string | null;
  supported_auth_methods?: string | null;
  created_at?: string;
  updated_at?: string;
  // Nested targets (when included)
  targets?: AssetTarget[];
  target_count?: number;
};

/**
 * Coverage Metrics Type
 */
export type CoverageMetrics = {
  totalEndpoints: number;
  testedEndpoints: number;
  vulnerableEndpoints: number;
  coverageRatio: number;
  density: number;
  totalFindings: number;
};

/**
 * SLA Finding Status
 */
export type SLAFindingStatus = {
  findingId: number;
  title: string;
  severity: string;
  submittedDate: string;
  slaDeadline: string; // ISO Date
  daysRemaining: number;
  status: "MET" | "MISSED" | "WARNING" | "On Track";
  breachLabel?: string | null;
  fixStartDeadline?: string | null;
  fixCompletionDeadline?: string | null;
  fixStartStatus?: string;
  fixCompletionStatus?: string;
};

/**
 * Asset SLA Status Type
 */
export type AssetSLAStatus = {
  assetId: string;
  criticality: AssetCriticality;
  totalFindings: number;
  breachedCount: number;
  metCount: number;
  pendingCount: number;
  complianceRate: number;
  findings: SLAFindingStatus[];
};

/**
 * Create Asset Request
 */
export interface CreateAssetRequest {
  name: string;
  description?: string;
  type: AssetType;
  criticality: AssetCriticality;
  owner_label?: string;
  data_sensitivity?: DataSensitivity;
  business_owner_id?: number;
  technical_owner_id?: number;
  tags?: string[];
  metadata?: Record<string, unknown>;
  auth_required?: boolean;
  access_surface_type?: string;
  role_context?: string;
  supported_auth_methods?: string;
}

/**
 * Update Asset Request
 */
export interface UpdateAssetRequest {
  name?: string;
  description?: string;
  type?: AssetType;
  status?: AssetStatus;
  criticality?: AssetCriticality;
  owner_label?: string;
  data_sensitivity?: DataSensitivity;
  business_owner_id?: number;
  technical_owner_id?: number;
  tags?: string[];
  metadata?: Record<string, unknown>;
  auth_required?: boolean;
  access_surface_type?: string;
  role_context?: string;
  supported_auth_methods?: string;
}

/**
 * Create Target Request
 */
export interface CreateTargetRequest {
  target_type: TargetType;
  target_value: string;
  environment: TargetEnvironment;
  notes?: string;
  tech_stack?: string[];
  metadata?: Record<string, unknown>;
}

/**
 * Update Target Request
 */
export interface UpdateTargetRequest {
  target_type?: TargetType;
  target_value?: string;
  environment?: TargetEnvironment;
  notes?: string;
  tech_stack?: string[];
  metadata?: Record<string, unknown>;
}

export type CompanyPagination = {
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
};

/**
 * Search for companies by name
 * @param query - The search query string
 * @returns Array of matching companies
 */
export const searchCompanies = async (query: string): Promise<Company[]> => {
  try {
    const response = await axios.get("/v2/companies/search", {
      params: { query }
    });
    return response.data || [];
  } catch (error: any) {
    console.error("Error searching companies:", error);
    if (error?.response?.data?.message) {
      toast.error(error.response.data.message);
    }
    return [];
  }
};

/**
 * Upload company logo
 * @param companyId - The company ID
 * @param logoFile - The logo file to upload
 * @returns The updated company with logo URL
 */
export const uploadCompanyLogo = async (
  companyId: number,
  logoFile: File
): Promise<Company | null> => {
  try {
    const formData = new FormData();
    formData.append("company_logo", logoFile);

    const response = await axios.post(
      `/v2/companies/${companyId}/logo`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data"
        }
      }
    );

    toast.success("Company logo uploaded successfully");
    return response.data.company;
  } catch (error: any) {
    console.error("Error uploading company logo:", error);
    if (error?.response?.data?.message) {
      toast.error(error.response.data.message);
    } else {
      toast.error("Failed to upload company logo");
    }
    return null;
  }
};

/**
 * Create a new company with optional logo
 * @param companyName - The company name
 * @param logoFile - Optional logo file
 * @returns The created company
 */
export const createCompany = async (
  companyName: string,
  logoFile?: File | null,
  subscriptionStartDate?: string | null,
  subscriptionEndDate?: string | null,
  subscriptionName?: string | null
): Promise<Company | null> => {
  try {
    const formData = new FormData();
    formData.append("company_name", companyName);
    if (logoFile) {
      formData.append("company_logo", logoFile);
    }
    if (subscriptionName) {
      formData.append("subscription_name", subscriptionName);
    }
    if (subscriptionStartDate) {
      formData.append("subscription_start_date", subscriptionStartDate);
    }
    if (subscriptionEndDate) {
      formData.append("subscription_end_date", subscriptionEndDate);
    }

    const response = await axios.post("/v2/companies", formData, {
      headers: {
        "Content-Type": "multipart/form-data"
      }
    });

    toast.success("Company created successfully");
    return response.data.company;
  } catch (error: any) {
    console.error("Error creating company:", error);
    if (error?.response?.data?.message) {
      toast.error(error.response.data.message);
    } else {
      toast.error("Failed to create company");
    }
    return null;
  }
};

/**
 * Get or create a company by name
 * @param companyName - The name of the company
 * @returns The company object (existing or newly created)
 */
export const getOrCreateCompany = async (
  companyName: string
): Promise<Company | undefined> => {
  try {
    const response = await axios.post(
      "/v2/companies/get-or-create",
      JSON.stringify({ company_name: companyName }),
      {
        headers: { "Content-Type": "application/json" }
      }
    );
    return response.data.company;
  } catch (error: any) {
    console.error("Error getting or creating company:", error);
    if (error?.response?.data?.message) {
      toast.error(error.response.data.message);
    }
    return undefined;
  }
};

/**
 * Get all companies with pagination (Admin only)
 * @param page - Page number
 * @param pageSize - Number of items per page
 * @param search - Search query
 * @param includeArchived - Whether to include archived companies
 * @returns Companies array and pagination info
 */
export const getAllCompanies = async (
  page: number = 1,
  pageSize: number = 10,
  search: string = "",
  includeArchived: boolean = false
): Promise<{ companies: Company[]; pagination: CompanyPagination } | null> => {
  try {
    const response = await axios.get("/v2/companies/admin/all", {
      params: { page, pageSize, search, includeArchived }
    });
    return response.data;
  } catch (error: any) {
    console.error("Error fetching companies:", error);
    if (error?.response?.data?.message) {
      toast.error(error.response.data.message);
    }
    return null;
  }
};

/**
 * Get a single company by ID (Admin only)
 * @param companyId - The company ID
 * @returns The company object with associated users
 */
export const getCompanyById = async (
  companyId: number
): Promise<Company | null> => {
  try {
    const response = await axios.get(`/v2/companies/admin/${companyId}`);
    return response.data.company;
  } catch (error: any) {
    console.error("Error fetching company:", error);
    if (error?.response?.data?.message) {
      toast.error(error.response.data.message);
    }
    return null;
  }
};

/**
 * Get current user's company (via users.company_id)
 */
export const getMyCompany = async (): Promise<Company | null> => {
  try {
    const response = await axios.get(`/v2/companies/my-company`);
    return response.data.company;
  } catch (error: any) {
    console.error("Error fetching my company:", error);
    return null;
  }
};

/**
 * Team member type for owner selection
 */
export interface TeamMember {
  user_id: number;
  username: string;
  email: string;
  display_name?: string;
  role: number;
  profile_picture?: string;
}

/**
 * Get team members for the current user's company
 * Returns users belonging to the same company (excluding current user)
 * Used for owner selection in asset forms
 */
export const getMyCompanyTeamMembers = async (): Promise<TeamMember[]> => {
  try {
    const response = await axios.get(`/v2/companies/my-company/team-members`);
    return response.data.teamMembers || [];
  } catch (error: any) {
    console.error("Error fetching team members:", error);
    return [];
  }
};

// ---------------------------------------------------------------------------
// Company-scoped Asset Groups & Assets inventory
// ---------------------------------------------------------------------------

/**
 * List all asset groups for a given company (business-side inventory view)
 */
export const listCompanyAssetGroups = async (
  companyId: number
): Promise<AssetGroup[]> => {
  try {
    const response = await axios.get(`/v2/companies/${companyId}/asset-groups`);
    return response.data?.data || [];
  } catch (error: any) {
    console.error("Error fetching company asset groups:", error);
    if (error?.response?.data?.message) {
      toast.error(error.response.data.message);
    }
    return [];
  }
};

/**
 * Create a new asset group under the given company
 */
export const createCompanyAssetGroup = async (
  companyId: number,
  payload: {
    name: string;
    description?: string;
    status?: string;
    business_owner_id?: number;
    technical_owner_id?: number;
  }
): Promise<AssetGroup | null> => {
  try {
    const response = await axios.post(
      `/v2/companies/${companyId}/asset-groups`,
      payload
    );

    toast.success("Asset group created successfully");
    return response.data?.data || null;
  } catch (error: any) {
    console.error("Error creating asset group:", error);
    if (error?.response?.data?.message) {
      toast.error(error.response.data.message);
    } else {
      toast.error("Failed to create asset group");
    }
    return null;
  }
};

/**
 * List assets for a specific asset group within a company
 */
export const listAssetGroupAssets = async (
  companyId: number,
  assetGroupId: number
): Promise<{ assetGroup: AssetGroup; assets: Asset[] } | null> => {
  try {
    const response = await axios.get(
      `/v2/companies/${companyId}/asset-groups/${assetGroupId}/assets`
    );
    const data = response.data?.data;
    if (!data) return null;
    return {
      assetGroup: data.assetGroup,
      assets: data.assets || []
    };
  } catch (error: any) {
    console.error("Error fetching asset group assets:", error);
    if (error?.response?.data?.message) {
      toast.error(error.response.data.message);
    }
    return null;
  }
};

/**
 * Create a new asset for an asset group within a company
 */
export const createAssetUnderAssetGroupForCompany = async (
  companyId: number,
  assetGroupId: number,
  payload: {
    name: string;
    type: AssetType | string;
    primary_identifier?: string;
    environment?: string;
    business_criticality?: string;
    data_sensitivity?: string;
    business_owner_id?: number;
    technical_owner_id?: number;
    tags?: string[];
  }
): Promise<Asset | null> => {
  try {
    const response = await axios.post(
      `/v2/companies/${companyId}/asset-groups/${assetGroupId}/assets`,
      payload
    );

    toast.success("Asset created successfully");
    return response.data?.data || null;
  } catch (error: any) {
    console.error("Error creating asset:", error);
    if (error?.response?.data?.message) {
      toast.error(error.response.data.message);
    } else {
      toast.error("Failed to create asset");
    }
    return null;
  }
};

/**
 * Update an asset for an asset group within a company
 */
export const updateAssetUnderAssetGroup = async (
  companyId: number,
  assetGroupId: number,
  assetId: number,
  payload: {
    name?: string;
    type?: AssetType | string;
    primary_identifier?: string;
    environment?: string;
    business_criticality?: string;
    data_sensitivity?: string;
    status?: AssetStatus;
    tags?: string[];
  }
): Promise<Asset | null> => {
  try {
    const response = await axios.put(
      `/v2/companies/${companyId}/asset-groups/${assetGroupId}/assets/${assetId}`,
      payload
    );

    toast.success("Asset updated successfully");
    return response.data?.data || null;
  } catch (error: any) {
    console.error("Error updating asset:", error);
    if (error?.response?.data?.message) {
      toast.error(error.response.data.message);
    } else {
      toast.error("Failed to update asset");
    }
    return null;
  }
};

/**
 * Delete an asset for an asset group within a company
 */
export const deleteAssetUnderAssetGroup = async (
  companyId: number,
  assetGroupId: number,
  assetId: number
): Promise<boolean> => {
  try {
    await axios.delete(
      `/v2/companies/${companyId}/asset-groups/${assetGroupId}/assets/${assetId}`
    );

    toast.success("Asset deleted successfully");
    return true;
  } catch (error: any) {
    console.error("Error deleting asset:", error);
    if (error?.response?.data?.message) {
      toast.error(error.response.data.message);
    } else {
      toast.error("Failed to delete asset");
    }
    return false;
  }
};

// ---------------------------------------------------------------------------
// NEW: Direct Asset Inventory API (CTB Framework)
// ---------------------------------------------------------------------------

/**
 * List all assets for a company (flat list, no asset groups)
 */
export const listCompanyAssets = async (
  companyId: number,
  options?: {
    status?: AssetStatus | "all";
    type?: AssetType;
    criticality?: AssetCriticality;
    search?: string;
  }
): Promise<Asset[]> => {
  try {
    const response = await axios.get(`/v2/companies/${companyId}/assets`, {
      params: options
    });
    return response.data?.data || [];
  } catch (error: any) {
    console.error("Error fetching company assets:", error);
    if (error?.response?.data?.message) {
      toast.error(error.response.data.message);
    }
    return [];
  }
};

/**
 * List all targets for the company (flat list from all assets)
 */
export const listAllCompanyTargets = async (): Promise<AssetTarget[]> => {
  try {
    const response = await axios.get(`/assets/all-targets`);
    return response.data || [];
  } catch (error: any) {
    console.error("Error fetching all company targets:", error);
    return [];
  }
};

/**
 * Get a single asset with its targets
 */
export const getCompanyAsset = async (
  companyId: number,
  assetId: number
): Promise<Asset | null> => {
  try {
    const response = await axios.get(
      `/v2/companies/${companyId}/assets/${assetId}`
    );
    return response.data?.data || null;
  } catch (error: any) {
    console.error("Error fetching asset:", error);
    if (error?.response?.data?.message) {
      toast.error(error.response.data.message);
    }
    return null;
  }
};

/**
 * Create a new asset
 */
export const createCompanyAsset = async (
  companyId: number,
  payload: CreateAssetRequest
): Promise<{ asset: Asset | null; warnings?: string[] }> => {
  try {
    const response = await axios.post(
      `/v2/companies/${companyId}/assets`,
      payload
    );
    toast.success("Asset created successfully");
    return {
      asset: response.data?.data || null,
      warnings: response.data?.warnings
    };
  } catch (error: any) {
    console.error("Error creating asset:", error);
    if (error?.response?.data?.message) {
      toast.error(error.response.data.message);
    } else {
      toast.error("Failed to create asset");
    }
    return { asset: null };
  }
};

/**
 * Get Asset Coverage Metrics
 */
export const getAssetCoverageMetrics = async (
  assetId: number
): Promise<CoverageMetrics | null> => {
  try {
    const response = await axios.get(`/v2/assets/${assetId}/coverage`);
    return response.data;
  } catch (error: any) {
    console.error("Error fetching coverage metrics:", error);
    return null;
  }
};

/**
 * Get Asset SLA Status
 */
export const getAssetSLAStatus = async (
  assetId: number
): Promise<AssetSLAStatus | null> => {
  try {
    const response = await axios.get(`/v2/assets/${assetId}/sla-status`);
    return response.data;
  } catch (error: any) {
    console.error("Error fetching SLA status:", error);
    return null;
  }
};

/**
 * Update an asset
 */
export const updateCompanyAsset = async (
  companyId: number,
  assetId: number,
  payload: UpdateAssetRequest
): Promise<Asset | null> => {
  try {
    const response = await axios.put(
      `/v2/companies/${companyId}/assets/${assetId}`,
      payload
    );
    toast.success("Asset updated successfully");
    return response.data?.data || null;
  } catch (error: any) {
    console.error("Error updating asset:", error);
    if (error?.response?.data?.message) {
      toast.error(error.response.data.message);
    } else {
      toast.error("Failed to update asset");
    }
    return null;
  }
};

/**
 * Delete (archive) an asset
 */
export const deleteCompanyAsset = async (
  companyId: number,
  assetId: number
): Promise<boolean> => {
  try {
    await axios.delete(`/v2/companies/${companyId}/assets/${assetId}`);
    toast.success("Asset archived successfully");
    return true;
  } catch (error: any) {
    console.error("Error deleting asset:", error);
    if (error?.response?.data?.message) {
      toast.error(error.response.data.message);
    } else {
      toast.error("Failed to archive asset");
    }
    return false;
  }
};

/**
 * Get asset analytics
 */
export const getCompanyAssetAnalytics = async (
  companyId: number,
  assetId: number
): Promise<{
  asset: Asset;
  targetCount: number;
  linkedPrograms: number;
  vulnerabilities: {
    critical: number;
    high: number;
    medium: number;
    low: number;
    informational: number;
  };
} | null> => {
  try {
    const response = await axios.get(
      `/v2/companies/${companyId}/assets/${assetId}/analytics`
    );
    return response.data?.data || null;
  } catch (error: any) {
    console.error("Error fetching asset analytics:", error);
    return null;
  }
};

/**
 * Get asset activity (reports + retests)
 */
export const getCompanyAssetActivity = async (
  companyId: number,
  assetId: number,
  options?: { limit?: number; offset?: number }
): Promise<{ reports: any[]; retests: any[] } | null> => {
  try {
    const response = await axios.get(
      `/v2/companies/${companyId}/assets/${assetId}/activity`,
      { params: options }
    );
    return response.data?.data || null;
  } catch (error: any) {
    console.error("Error fetching asset activity:", error);
    return null;
  }
};

// ---------------------------------------------------------------------------
// Asset Target API
// ---------------------------------------------------------------------------

/**
 * List all targets for an asset
 */
export const listAssetTargets = async (
  companyId: number,
  assetId: number
): Promise<{ asset: Asset; targets: AssetTarget[] } | null> => {
  try {
    const response = await axios.get(
      `/v2/companies/${companyId}/assets/${assetId}/targets`
    );
    return response.data?.data || null;
  } catch (error: any) {
    console.error("Error fetching asset targets:", error);
    if (error?.response?.data?.message) {
      toast.error(error.response.data.message);
    }
    return null;
  }
};

/**
 * Get a single target
 */
export const getAssetTarget = async (
  companyId: number,
  assetId: number,
  targetId: number
): Promise<AssetTarget | null> => {
  try {
    const response = await axios.get(
      `/v2/companies/${companyId}/assets/${assetId}/targets/${targetId}`
    );
    return response.data?.data || null;
  } catch (error: any) {
    console.error("Error fetching target:", error);
    return null;
  }
};

/**
 * Create a new target
 */
export const createAssetTarget = async (
  companyId: number,
  assetId: number,
  payload: CreateTargetRequest
): Promise<{ target: AssetTarget | null; warnings?: string[] }> => {
  try {
    const response = await axios.post(
      `/v2/companies/${companyId}/assets/${assetId}/targets`,
      payload
    );
    toast.success("Target created successfully");
    return {
      target: response.data?.data || null,
      warnings: response.data?.warnings
    };
  } catch (error: any) {
    console.error("Error creating target:", error);
    if (error?.response?.data?.message) {
      toast.error(error.response.data.message);
    } else if (error?.response?.data?.errors) {
      toast.error(error.response.data.errors.join(", "));
    } else {
      toast.error("Failed to create target");
    }
    return { target: null };
  }
};

/**
 * Update a target
 */
export const updateAssetTarget = async (
  companyId: number,
  assetId: number,
  targetId: number,
  payload: UpdateTargetRequest
): Promise<AssetTarget | null> => {
  try {
    const response = await axios.put(
      `/v2/companies/${companyId}/assets/${assetId}/targets/${targetId}`,
      payload
    );
    toast.success("Target updated successfully");
    return response.data?.data || null;
  } catch (error: any) {
    console.error("Error updating target:", error);
    if (error?.response?.data?.message) {
      toast.error(error.response.data.message);
    } else {
      toast.error("Failed to update target");
    }
    return null;
  }
};

/**
 * Delete a target
 */
export const deleteAssetTarget = async (
  companyId: number,
  assetId: number,
  targetId: number
): Promise<boolean> => {
  try {
    await axios.delete(
      `/v2/companies/${companyId}/assets/${assetId}/targets/${targetId}`
    );
    toast.success("Target deleted successfully");
    return true;
  } catch (error: any) {
    console.error("Error deleting target:", error);
    if (error?.response?.data?.message) {
      toast.error(error.response.data.message);
    } else {
      toast.error("Failed to delete target");
    }
    return false;
  }
};

/**
 * Verify a target (check reachability/resolution)
 */
export const verifyAssetTarget = async (
  companyId: number,
  assetId: number,
  targetId: number
): Promise<{
  verification_state: TargetVerificationState;
  verified_at: string;
  message: string;
} | null> => {
  try {
    const response = await axios.post(
      `/v2/companies/${companyId}/assets/${assetId}/targets/${targetId}/verify`
    );
    const data = response.data?.data;
    if (data?.verification_state === "VERIFIED") {
      toast.success("Target verified successfully");
    } else if (data?.verification_state === "FAILED") {
      toast.error(response.data?.message || "Target verification failed");
    }
    return {
      verification_state: data?.verification_state,
      verified_at: data?.verified_at,
      message: response.data?.message
    };
  } catch (error: any) {
    console.error("Error verifying target:", error);
    toast.error("Failed to verify target");
    return null;
  }
};

/**
 * Bulk create targets - Creates multiple targets at once
 * Returns results with success/failure for each
 */
export const bulkCreateAssetTargets = async (
  companyId: number,
  assetId: number,
  targets: CreateTargetRequest[]
): Promise<{
  success: { target: AssetTarget; index: number }[];
  failed: { error: string; index: number; value: string }[];
}> => {
  const results = {
    success: [] as { target: AssetTarget; index: number }[],
    failed: [] as { error: string; index: number; value: string }[]
  };

  // Process targets sequentially to avoid overwhelming the server
  for (let i = 0; i < targets.length; i++) {
    try {
      const response = await axios.post(
        `/v2/companies/${companyId}/assets/${assetId}/targets`,
        targets[i]
      );
      if (response.data?.data) {
        results.success.push({ target: response.data.data, index: i });
      }
    } catch (error: any) {
      const errorMsg = error?.response?.data?.message ||
        error?.response?.data?.errors?.join(", ") ||
        "Failed to create target";
      results.failed.push({
        error: errorMsg,
        index: i,
        value: targets[i].target_value
      });
    }
  }

  if (results.success.length > 0) {
    toast.success(`${results.success.length} targets created successfully`);
  }
  if (results.failed.length > 0) {
    toast.error(`${results.failed.length} targets failed to import`);
  }

  return results;
};

/**
 * Bulk delete targets
 */
export const bulkDeleteAssetTargets = async (
  companyId: number,
  assetId: number,
  targetIds: number[]
): Promise<{ success: number[]; failed: number[] }> => {
  const results = { success: [] as number[], failed: [] as number[] };

  for (const targetId of targetIds) {
    try {
      await axios.delete(
        `/v2/companies/${companyId}/assets/${assetId}/targets/${targetId}`
      );
      results.success.push(targetId);
    } catch {
      results.failed.push(targetId);
    }
  }

  if (results.success.length > 0) {
    toast.success(`${results.success.length} targets deleted`);
  }
  if (results.failed.length > 0) {
    toast.error(`${results.failed.length} targets failed to delete`);
  }

  return results;
};

/**
 * Update current user's company profile details
 */
export const updateMyCompanyDetails = async (payload: Partial<Company>): Promise<Company | null> => {
  try {
    const response = await axios.put(`/v2/companies/my-company/details`, payload);
    return response.data.company;
  } catch (error: any) {
    console.error("Error updating my company details:", error);
    if (error?.response?.data?.message) {
      toast.error(error.response.data.message);
    } else {
      toast.error("Failed to update company details");
    }
    return null;
  }
};

// Admin: update company profile fields by company_id
export const updateCompanyDetailsAdmin = async (
  companyId: number,
  payload: Partial<Company>
): Promise<Company | null> => {
  try {
    const response = await axios.put(`/v2/companies/admin/${companyId}/details`, payload);
    return response.data.company;
  } catch (error: any) {
    console.error("Error updating company details (admin):", error);
    if (error?.response?.data?.message) {
      toast.error(error.response.data.message);
    } else {
      toast.error("Failed to update company details");
    }
    return null;
  }
};

// Admin: list company names (id + name)
export const listCompanyNamesAdmin = async (): Promise<Array<{ company_id: number; company_name: string }>> => {
  try {
    const axios = (await import("../../api/axios")).default;
    const resp = await axios.get("/v2/companies/admin/names");
    return resp.data.companies || [];
  } catch (error: any) {
    console.error("Error listing company names:", error);
    return [];
  }
};

// Admin: resolve company and owner by company_name
export const resolveCompanyByNameAdmin = async (
  companyName: string
): Promise<{ company: Company | null; owner: { user_id: number; email: string } | null }> => {
  try {
    const axios = (await import("../../api/axios")).default;
    const resp = await axios.get(`/v2/companies/admin/resolve-by-name`, { params: { company_name: companyName } });
    return { company: resp.data.company || null, owner: resp.data.owner || null };
  } catch (error: any) {
    console.error("Error resolving company by name:", error);
    return { company: null, owner: null };
  }
};

/**
 * Get program statistics for all companies (Admin only)
 * Optimized endpoint that returns program counts, latest end dates, and earliest start dates
 * @param search - Optional search query
 * @returns Object mapping company IDs to their program statistics
 */
export const getCompanyProgramStats = async (
  search: string = ""
): Promise<Record<number, {
  programCount: number;
  latestProgramEndDate: string | null;
  earliestProgramStartDate: string | null;
}> | null> => {
  try {
    const response = await axios.get("/v2/companies/admin/program-stats", {
      params: { search }
    });
    return response.data.stats || {};
  } catch (error: any) {
    console.error("Error fetching company program stats:", error);
    if (error?.response?.data?.message) {
      toast.error(error.response.data.message);
    }
    return null;
  }
};

/**
 * Update company name and/or logo (Admin only)
 * @param companyId - The company ID
 * @param companyName - Optional new company name
 * @param logoFile - Optional new logo file
 * @param subscriptionType - Optional subscription type
 * @returns The updated company object
 */
export const updateCompany = async (
  companyId: number,
  companyName: string,
  logoFile: File | null,
  subscriptionType: SubscriptionType | null,
  quickRunsQuota: number | null,
  comprehensiveRunsQuota: number | null,
  quickRunDuration: number | null,
  comprehensiveRunDuration: number | null,
  isArchived: boolean | null = null,
  subscriptionStartDate: string | null = null,
  subscriptionEndDate: string | null = null
): Promise<Company | null> => {
  try {
    const formData = new FormData();
    formData.append("company_name", companyName);
    if (logoFile) {
      formData.append("company_logo", logoFile);
    }
    if (subscriptionType !== undefined) {
      formData.append("subscription_type", subscriptionType || "null");
    }
    if (quickRunsQuota !== null) {
      formData.append("quick_runs_quota", quickRunsQuota.toString());
    }
    if (comprehensiveRunsQuota !== null) {
      formData.append(
        "comprehensive_runs_quota",
        comprehensiveRunsQuota.toString()
      );
    }
    if (quickRunDuration !== null) {
      formData.append("quick_run_duration", quickRunDuration.toString());
    }
    if (comprehensiveRunDuration !== null) {
      formData.append(
        "comprehensive_run_duration",
        comprehensiveRunDuration.toString()
      );
    }
    if (isArchived !== null) {
      formData.append("is_archived", isArchived.toString());
    }
    if (subscriptionStartDate !== null) {
      formData.append("subscription_start_date", subscriptionStartDate || "null");
    }
    if (subscriptionEndDate !== null) {
      formData.append("subscription_end_date", subscriptionEndDate || "null");
    }

    const response = await axios.put(
      `/v2/companies/admin/${companyId}`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data"
        }
      }
    );

    toast.success("Company updated successfully");
    return response.data.company;
  } catch (error: any) {
    console.error("Error updating company:", error);
    if (error?.response?.data?.message) {
      toast.error(error.response.data.message);
    } else {
      toast.error("Failed to update company");
    }
    return null;
  }
};

/**
 * Archive or unarchive a company (Admin only)
 * @param companyId - The company ID
 * @param isArchived - Whether to archive (true) or unarchive (false)
 * @returns The updated company object
 */
export const toggleArchiveCompany = async (
  companyId: number,
  isArchived: boolean
): Promise<Company | null> => {
  try {
    const response = await axios.patch(
      `/v2/companies/admin/${companyId}/archive`,
      { is_archived: isArchived }
    );
    toast.success(
      isArchived ? "Company archived successfully" : "Company unarchived successfully"
    );
    return response.data.company;
  } catch (error: any) {
    console.error("Error toggling archive status:", error);
    if (error?.response?.data?.message) {
      toast.error(error.response.data.message);
    } else {
      toast.error("Failed to update company archive status");
    }
    return null;
  }
};

/**
 * Pin or unpin a company (Admin only)
 * @param companyId - The company ID
 * @param isPinned - Whether to pin (true) or unpin (false)
 * @returns The updated company object
 */
export const togglePinCompany = async (
  companyId: number,
  isPinned: boolean
): Promise<Company | null> => {
  try {
    const response = await axios.patch(
      `/v2/companies/admin/${companyId}/pin`,
      { is_pinned: isPinned }
    );
    toast.success(
      isPinned ? "Company pinned successfully" : "Company unpinned successfully"
    );
    return response.data.company;
  } catch (error: any) {
    console.error("Error toggling pin status:", error);
    if (error?.response?.data?.message) {
      toast.error(error.response.data.message);
    } else {
      toast.error("Failed to update company pin status");
    }
    return null;
  }
};

/**
 * Delete a company (Admin only)
 * @param companyId - The company ID
 * @returns Success boolean
 */
export const deleteCompany = async (companyId: number): Promise<boolean> => {
  try {
    await axios.delete(`/v2/companies/admin/${companyId}`);
    toast.success("Company deleted successfully");
    return true;
  } catch (error: any) {
    console.error("Error deleting company:", error);
    if (error?.response?.data?.message) {
      toast.error(error.response.data.message);
    } else {
      toast.error("Failed to delete company");
    }
    return false;
  }
};

/**
 * Get all business users (Admin only)
 * @param search - Optional search query
 * @returns Array of business users
 */
export const getBusinessUsers = async (
  search: string = ""
): Promise<any[] | null> => {
  try {
    const response = await axios.get("/v2/companies/admin/business-users", {
      params: { search }
    });
    return response.data.users;
  } catch (error: any) {
    console.error("Error fetching business users:", error);
    if (error?.response?.data?.message) {
      toast.error(error.response.data.message);
    }
    return null;
  }
};

/**
 * Link a business user to a company (Admin only)
 * @param companyId - The company ID
 * @param userId - The user ID
 * @returns Success boolean
 */
export const linkUserToCompany = async (
  companyId: number,
  userId: number
): Promise<boolean> => {
  try {
    await axios.post(`/v2/companies/admin/${companyId}/link-user`, {
      user_id: userId
    });
    toast.success("User linked to company successfully");
    return true;
  } catch (error: any) {
    console.error("Error linking user to company:", error);
    if (error?.response?.data?.message) {
      toast.error(error.response.data.message);
    } else {
      toast.error("Failed to link user to company");
    }
    return false;
  }
};

/**
 * Unlink a business user from their company (Admin only)
 * @param userId - The user ID
 * @returns Success boolean
 */
export const unlinkUserFromCompany = async (
  userId: number
): Promise<boolean> => {
  try {
    await axios.delete(`/v2/companies/admin/users/${userId}/unlink`);
    toast.success("User unlinked from company successfully");
    return true;
  } catch (error: any) {
    console.error("Error unlinking user from company:", error);
    if (error?.response?.data?.message) {
      toast.error(error.response.data.message);
    } else {
      toast.error("Failed to unlink user from company");
    }
    return false;
  }
};

// ---------------------------------------------------------------------------
// Application Analytics types
// ---------------------------------------------------------------------------

export interface EnvironmentComparison {
  targetId: number;
  targetUrl: string;
  targetType: string;
  environment: string;
  openVulns: number;
  bySeverity: {
    critical: number;
    high: number;
    medium: number;
    low: number;
  };
  isInScope?: boolean;
  lastTestedDate?: string;
}

export interface TargetAudit {
  targetId: number;
  targetValue: string;
  environment: string;
  testCount: number;
}

export type ApplicationAnalytics = {
  severityBreakdown: {
    CRITICAL: number;
    HIGH: number;
    MEDIUM: number;
    LOW: number;
  };
  resolutionBreakdown: {
    open: number;
    in_progress: number;
    fixed: number;
    accepted_risk: number;
    wont_fix: number;
  };
  ageBreakdown: {
    week: number;
    month: number;
    quarter: number;
    older: number;
  };
  testSessions: Array<{
    program_id: number;
    name: string;
    start_date: string | null;
    end_date: string | null;
    status: string;
    findings: number;
  }>;
  vulnerabilityTrend: Array<{
    month: string;
    critical: number;
    high: number;
    medium: number;
    low: number;
  }>;
  fixingTrend: Array<{
    month: string;
    opened: number;
    fixed: number;
  }>;
  riskHistory?: AssetRiskHistoryPoint[];
  securityEvolution?: Array<{
    programId: number;
    programTitle: string;
    endDate: string;
    severityCounts: {
      Critical: number;
      High: number;
      Medium: number;
      Low: number;
      Total: number;
    };
  }>;
  coverageMetrics?: {
    programId: number;
    programTitle: string;
    totalEndpoints: number;
    testedEndpoints: number;
    findingsLinked: number;
    coveragePercent: number;
    lastTestedDate?: string;
  };
  auditTrail?: Array<{
    programId: number;
    programTitle: string;
    startDate: string;
    endDate: string;
    isActivated: boolean;
    scopeState: string;
    targetsIncluded: string | number;
    targets?: Array<{
      targetId: number;
      targetValue: string;
      environment: string;
    }>;
  }>;
  targetAudit?: TargetAudit[];
  environmentComparison?: EnvironmentComparison[];
  totalReports: number;
};

/**
 * Get analytics data for an asset group
 * @param companyId - The company ID
 * @param assetGroupId - The asset group ID
 * @returns Analytics data or null
 */
export const getAssetGroupAnalytics = async (
  companyId: number,
  assetGroupId: number
): Promise<ApplicationAnalytics | null> => {
  try {
    const response = await axios.get<ApplicationAnalytics>(
      `/v2/companies/${companyId}/asset-groups/${assetGroupId}/analytics`
    );
    return response.data;
  } catch (error: any) {
    console.error("Error fetching asset group analytics:", error);
    return null;
  }
};

/**
 * Get analytics for a specific asset
 * @param companyId - The company ID
 * @param assetGroupId - The asset group ID
 * @param assetId - The asset ID
 * @returns Analytics data or null
 */
export const getAssetAnalytics = async (
  companyId: number,
  assetGroupId: number,
  assetId: number
): Promise<ApplicationAnalytics | null> => {
  try {
    const response = await axios.get<ApplicationAnalytics>(
      `/v2/companies/${companyId}/asset-groups/${assetGroupId}/assets/${assetId}/analytics`
    );
    return response.data;
  } catch (error: any) {
    console.error("Error fetching asset analytics:", error);
    return null;
  }
};

/**
 * Get analytics for a specific asset directly (no asset group)
 */
export const getAssetAnalyticsDirect = async (
  companyId: number,
  assetId: number
): Promise<ApplicationAnalytics | null> => {
  try {
    const response = await axios.get<ApplicationAnalytics>(
      `/v2/companies/${companyId}/assets/${assetId}/analytics`
    );
    return response.data;
  } catch (error: any) {
    console.error("Error fetching direct asset analytics:", error);
    return null;
  }
};

export interface AssetActivityReport {
  report_id: number;
  title: string;
  severity: string;
  state: string;
  submitted_date: string;
  scope: string;
  researcher: {
    user_id: number;
    username: string;
    profile_image: string | null;
  } | null;
}

export interface AssetActivityRetest {
  retest_id: number;
  report_id: number;
  report_title: string | null;
  status: string;
  comments: string | null;
  created_at: string;
  updated_at: string;
}

export interface AssetActivity {
  reports: AssetActivityReport[];
  retests: AssetActivityRetest[];
  assetCreatedAt: string;
  assetUpdatedAt: string;
}

/**
 * Get activity data for a specific asset (reports and retests)
 * @param companyId - The company ID
 * @param assetGroupId - The asset group ID
 * @param assetId - The asset ID
 * @returns Activity data or null
 */
export const getAssetActivity = async (
  companyId: number,
  assetGroupId: number,
  assetId: number
): Promise<AssetActivity | null> => {
  try {
    const response = await axios.get<AssetActivity>(
      `/v2/companies/${companyId}/asset-groups/${assetGroupId}/assets/${assetId}/activity`
    );
    return response.data;
  } catch (error: any) {
    console.error("Error fetching asset activity:", error);
    return null;
  }
};

export type AssetGroupSeveritySummary = {
  asset_group_id: number;
  severity: {
    critical: number;
    high: number;
    medium: number;
    low: number;
  };
};

/**
 * Get severity summary for all asset groups in a company
 * @param companyId - The company ID
 * @returns Array of asset group severity summaries
 */
export const getAssetGroupsSeveritySummary = async (
  companyId: number
): Promise<AssetGroupSeveritySummary[]> => {
  try {
    const response = await axios.get<{ assetGroups: AssetGroupSeveritySummary[] }>(
      `/v2/companies/${companyId}/asset-groups/severity-summary`
    );
    return response.data.assetGroups;
  } catch (error: any) {
    console.error("Error fetching asset groups severity summary:", error);
    return [];
  }
};

// ============================================
// Target Resource API Functions
// ============================================

/**
 * List all resources for a target
 */
export const listTargetResources = async (
  targetId: number,
  filters?: {
    resource_type?: ResourceType;
    status?: ResourceStatus;
    search?: string;
  }
): Promise<TargetResource[]> => {
  try {
    const params = new URLSearchParams({ target_id: String(targetId) });
    if (filters?.resource_type) params.append("resource_type", filters.resource_type);
    if (filters?.status) params.append("status", filters.status);
    if (filters?.search) params.append("search", filters.search);

    const response = await axios.get(`/v2/target_resource?${params.toString()}`);
    return response.data?.data || [];
  } catch (error: any) {
    console.error("Error fetching target resources:", error);
    if (error?.response?.data?.message) {
      toast.error(error.response.data.message);
    }
    return [];
  }
};

/**
 * Get a single target resource
 */
export const getTargetResource = async (
  resourceId: number
): Promise<TargetResource | null> => {
  try {
    const response = await axios.get(`/v2/target_resource/${resourceId}`);
    return response.data?.data || null;
  } catch (error: any) {
    console.error("Error fetching target resource:", error);
    return null;
  }
};

/**
 * Create a new target resource
 */
export const createTargetResource = async (
  payload: CreateTargetResourceRequest
): Promise<TargetResource | null> => {
  try {
    const response = await axios.post("/v2/target_resource", payload);
    toast.success("Resource created successfully");
    return response.data?.data || null;
  } catch (error: any) {
    console.error("Error creating target resource:", error);
    if (error?.response?.data?.message) {
      toast.error(error.response.data.message);
    } else {
      toast.error("Failed to create resource");
    }
    return null;
  }
};

/**
 * Update a target resource
 */
export const updateTargetResource = async (
  resourceId: number,
  payload: UpdateTargetResourceRequest
): Promise<TargetResource | null> => {
  try {
    const response = await axios.put(`/v2/target_resource/${resourceId}`, payload);
    toast.success("Resource updated successfully");
    return response.data?.data || null;
  } catch (error: any) {
    console.error("Error updating target resource:", error);
    if (error?.response?.data?.message) {
      toast.error(error.response.data.message);
    } else {
      toast.error("Failed to update resource");
    }
    return null;
  }
};

/**
 * Delete a target resource
 */
export const deleteTargetResource = async (
  resourceId: number
): Promise<boolean> => {
  try {
    await axios.delete(`/v2/target_resource/${resourceId}`);
    toast.success("Resource deleted successfully");
    return true;
  } catch (error: any) {
    console.error("Error deleting target resource:", error);
    if (error?.response?.data?.message) {
      toast.error(error.response.data.message);
    } else {
      toast.error("Failed to delete resource");
    }
    return false;
  }
};

/**
 * Restore a deleted asset target
 */
export const restoreAssetTarget = async (
  companyId: number,
  assetId: number,
  targetId: number
): Promise<AssetTarget | null> => {
  try {
    const response = await axios.post(
      `/v2/companies/${companyId}/assets/${assetId}/targets/${targetId}/restore`
    );
    toast.success("Target restored successfully");
    return response.data?.data || null;
  } catch (error: any) {
    console.error("Error restoring asset target:", error);
    if (error?.response?.data?.message) {
      toast.error(error.response.data.message);
    } else {
      toast.error("Failed to restore target");
    }
    return null;
  }
};

/**
 * Bulk create target resources
 */
export const bulkCreateTargetResources = async (
  targetId: number,
  resources: Array<{
    value: string;
    resource_type: ResourceType;
    status?: ResourceStatus;
    http_method?: string;
    notes?: string;
  }>
): Promise<{
  created: TargetResource[];
  errors: Array<{ index: number; value: string; error: string }>;
}> => {
  try {
    const response = await axios.post("/v2/target_resource/bulk", {
      target_id: targetId,
      resources
    });
    const data = response.data?.data || { created: [], errors: [] };
    if (data.created?.length > 0) {
      toast.success(`${data.created.length} resources created`);
    }
    if (data.errors?.length > 0) {
      toast.error(`${data.errors.length} resources failed`);
    }
    return data;
  } catch (error: any) {
    console.error("Error bulk creating resources:", error);
    if (error?.response?.data?.message) {
      toast.error(error.response.data.message);
    } else {
      toast.error("Failed to create resources");
    }
    return { created: [], errors: [] };
  }
};

/**
 * Bulk delete target resources
 */
export const bulkDeleteTargetResources = async (
  resourceIds: number[]
): Promise<{ deleted_count: number }> => {
  try {
    const response = await axios.delete("/v2/target_resource/bulk", {
      data: { resource_ids: resourceIds }
    });
    const count = response.data?.deleted_count || 0;
    if (count > 0) {
      toast.success(`${count} resources deleted`);
    }
    return { deleted_count: count };
  } catch (error: any) {
    console.error("Error bulk deleting resources:", error);
    if (error?.response?.data?.message) {
      toast.error(error.response.data.message);
    } else {
      toast.error("Failed to delete resources");
    }
    return { deleted_count: 0 };
  }
};

// ---------------------------------------------------------------------------
// Asset Global Context API
// ---------------------------------------------------------------------------

/**
 * Get global context for an asset
 */
export const getAssetGlobalContext = async (
  companyId: number,
  assetId: number
): Promise<AssetGlobalContext | null> => {
  try {
    const response = await axios.get(
      `/v2/companies/${companyId}/assets/${assetId}/global-context`
    );
    return response.data?.data || { product_overview: "", files: [] };
  } catch (error: any) {
    console.error("Error fetching global context:", error);
    return null;
  }
};

/**
 * Update global context (product overview text)
 */
export const updateAssetGlobalContext = async (
  companyId: number,
  assetId: number,
  productOverview: string
): Promise<AssetGlobalContext | null> => {
  try {
    const response = await axios.put(
      `/v2/companies/${companyId}/assets/${assetId}/global-context`,
      { product_overview: productOverview }
    );
    toast.success("Product overview updated");
    return response.data?.data || null;
  } catch (error: any) {
    console.error("Error updating global context:", error);
    if (error?.response?.data?.message) {
      toast.error(error.response.data.message);
    } else {
      toast.error("Failed to update product overview");
    }
    return null;
  }
};

/**
 * Upload a file to asset global context
 */
export const uploadGlobalContextFile = async (
  companyId: number,
  assetId: number,
  file: File,
  fileType: GlobalContextFileType,
  description?: string
): Promise<GlobalContextFile | null> => {
  try {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("file_type", fileType);
    if (description) {
      formData.append("description", description);
    }

    const response = await axios.post(
      `/v2/companies/${companyId}/assets/${assetId}/global-context/files`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data"
        }
      }
    );
    toast.success("File uploaded successfully");
    return response.data?.data || null;
  } catch (error: any) {
    console.error("Error uploading file:", error);
    if (error?.response?.data?.message) {
      toast.error(error.response.data.message);
    } else {
      toast.error("Failed to upload file");
    }
    return null;
  }
};

/**
 * Delete a file from asset global context
 */
export const deleteGlobalContextFile = async (
  companyId: number,
  assetId: number,
  fileId: string
): Promise<boolean> => {
  try {
    await axios.delete(
      `/v2/companies/${companyId}/assets/${assetId}/global-context/files/${fileId}`
    );
    toast.success("File deleted");
    return true;
  } catch (error: any) {
    console.error("Error deleting file:", error);
    if (error?.response?.data?.message) {
      toast.error(error.response.data.message);
    } else {
      toast.error("Failed to delete file");
    }
    return false;
  }
};

// ============================================
// Asset Risk Analytics Types & API
// ============================================

export interface RiskHeatmapCell {
  criticality: string;
  severity: string;
  count: number;
  riskScore: number;
  assets: string[];
}

export interface RiskHeatmapSummary {
  totalRisk: number;
  criticalCount: number;
  highCount: number;
  assetCount: number;
}

export interface TopRiskyAsset {
  assetId: number;
  name: string;
  criticality: string;
  type: string;
  openRiskScore: number;
  severityCounts: {
    CRITICAL: number;
    HIGH: number;
    MEDIUM: number;
    LOW: number;
  };
  reportCount: number;
}

export interface RiskTrendPoint {
  month: string;
  totalRisk: number;
  openCount: number;
  closedCount: number;
}

export interface AssetRiskAnalyticsData {
  heatmap: {
    cells: RiskHeatmapCell[];
    summary: RiskHeatmapSummary;
  };
  topRiskyAssets: TopRiskyAsset[];
  riskTrend: RiskTrendPoint[];
}

/**
 * Get asset risk analytics for a company
 * Returns heatmap data, top risky assets, and risk trend over time
 */
export const getAssetRiskAnalytics = async (
  companyId: number,
  months?: number
): Promise<AssetRiskAnalyticsData | null> => {
  try {
    const params = months ? { months: months.toString() } : {};
    const response = await axios.get(
      `/v2/companies/${companyId}/assets/risk-analytics`,
      { params }
    );
    return response.data?.data || null;
  } catch (error: any) {
    console.error("Error fetching asset risk analytics:", error);
    return null;
  }
};

// ============================================================================
// Heatmap Cell Reports Types & API
// ============================================================================

export interface HeatmapCellReport {
  report_id: number;
  title: string;
  severity: string;
  state: string;
  submitted_date: string;
  asset_name: string;
}

export interface HeatmapCellReportsResponse {
  criticality: string;
  severity: string;
  count: number;
  reports: HeatmapCellReport[];
}

/**
 * Get reports for a specific heatmap cell (criticality × severity)
 */
export const getHeatmapCellReports = async (
  companyId: number,
  criticality: string,
  severity: string
): Promise<HeatmapCellReportsResponse | null> => {
  try {
    const response = await axios.get(
      `/v2/companies/${companyId}/assets/risk-analytics/cell-reports`,
      { params: { criticality, severity } }
    );
    return response.data?.data || null;
  } catch (error: any) {
    console.error("Error fetching heatmap cell reports:", error);
    return null;
  }
};

// ============================================================================
// Single Asset Risk Analytics Types
// ============================================================================

export interface AssetSeverityBreakdown {
  count: number;
  riskScore: number;
}

export interface AssetRiskHistoryPoint {
  month: string;
  openCount: number;
  openRiskScore: number;
  newCount: number;
  resolvedCount: number;
}

export interface EnvironmentComparison {
  targetId: number;
  targetUrl: string;
  targetType: string;
  openVulns: number;
  riskScore: number;
  bySeverity: {
    critical: number;
    high: number;
    medium: number;
    low: number;
  };
}

export interface AssetProgramHistory {
  programId: number;
  programName: string;
  startDate: string;
  endDate: string | null;
  status: string;
  bugCount: number;
}

export interface SingleAssetRiskAnalyticsData {
  assetId: number;
  assetName: string;
  criticality: string;
  criticalityWeight: number;
  summary: {
    totalOpenVulns: number;
    totalOpenRiskScore: number;
    targetCount: number;
    programCount: number;
  };
  severityBreakdown: {
    critical: AssetSeverityBreakdown;
    high: AssetSeverityBreakdown;
    medium: AssetSeverityBreakdown;
    low: AssetSeverityBreakdown;
  };
  riskHistory: AssetRiskHistoryPoint[];
  environmentComparison: EnvironmentComparison[];
  programHistory: AssetProgramHistory[];
}

/**
 * Get risk analytics for a single asset
 * Returns risk history, severity breakdown, environment comparison, and program history
 */
export const getSingleAssetRiskAnalytics = async (
  companyId: number,
  assetId: number,
  months?: number
): Promise<SingleAssetRiskAnalyticsData | null> => {
  try {
    const params = months ? { months: months.toString() } : {};
    const response = await axios.get(
      `/v2/companies/${companyId}/assets/${assetId}/risk-analytics`,
      { params }
    );
    return response.data?.data || null;
  } catch (error: any) {
    console.error("Error fetching single asset risk analytics:", error);
    return null;
  }
};
// ===========================================================================
// Company Integration Settings (Slack / Jira)
// ===========================================================================

export type CompanyIntegrations = {
  /** Whether a Slack webhook is saved — the URL is never returned */
  slack_webhook_configured: boolean;
  jira_url: string | null;
  jira_email: string | null;
  /** Not the actual token — just a flag indicating one is saved */
  jira_api_token_set: boolean;
  jira_project_key: string | null;
  /** Whether a Teams webhook is saved — the URL is never returned */
  teams_webhook_configured: boolean;
  github_token_set: boolean;
  github_repo: string | null;
  gitlab_token_set: boolean;
  gitlab_project: string | null;
  gitlab_url: string | null;
};

export type CompanyIntegrationsPayload = {
  slack_webhook_url?: string;
  jira_url?: string;
  jira_email?: string;
  /** Pass the actual token to save, or "" to clear */
  jira_api_token?: string;
  jira_project_key?: string;
  teams_webhook_url?: string;
  github_token?: string;
  github_repo?: string;
  gitlab_token?: string;
  gitlab_project?: string;
  gitlab_url?: string;
};

/**
 * Fetch the current user's company integration settings.
 * Webhook URLs and API tokens are NOT returned (only configured flags).
 */
export const getMyCompanyIntegrations = async (): Promise<CompanyIntegrations | null> => {
  try {
    const res = await axios.get("/v2/companies/my-company/integrations");
    return res.data?.integrations ?? null;
  } catch (error: any) {
    console.error("Error fetching company integrations:", error);
    return null;
  }
};

/**
 * Save company-level Slack / Jira integration settings.
 * Pass only the fields you want to update.
 * Send "" (empty string) to clear a field.
 */
export const updateMyCompanyIntegrations = async (
  payload: CompanyIntegrationsPayload
): Promise<{ success: boolean; message?: string }> => {
  try {
    const res = await axios.put("/v2/companies/my-company/integrations", payload);
    return { success: true, message: res.data?.message };
  } catch (error: any) {
    const msg = error?.response?.data?.message || "Failed to save integrations";
    return { success: false, message: msg };
  }
};


/**
 * Subscription record for a company
 */
export type Subscription = {
  subscription_id: number;
  company_id: number;
  subscription_name?: string | null;
  subscription_type: string;
  start_date: string;
  end_date: string;
  quick_runs_quota: number;
  comprehensive_runs_quota: number;
  quick_run_duration: number;
  comprehensive_run_duration: number;
  created_at?: string;
  updated_at?: string;
}

/**
 * Get all subscriptions for a company (Admin only)
 */
export const getCompanySubscriptions = async (companyId: number): Promise<Subscription[]> => {
  try {
    const response = await axios.get(`/v2/companies/admin/${companyId}/subscriptions`);
    return response.data || [];
  } catch (error: any) {
    console.error("Error fetching company subscriptions:", error);
    if (error?.response?.data?.message) {
      toast.error(error.response.data.message);
    }
    return [];
  }
};

/**
 * Create a new subscription for a company (Admin only)
 */
export const createSubscription = async (companyId: number, data: Partial<Subscription>): Promise<Subscription | null> => {
  try {
    const response = await axios.post(`/v2/companies/admin/${companyId}/subscriptions`, data);
    toast.success("Subscription added successfully");
    return response.data;
  } catch (error: any) {
    console.error("Error creating subscription:", error);
    toast.error(error?.response?.data?.message || "Failed to add subscription");
    return null;
  }
};

/**
 * Update a subscription (Admin only)
 */
export const updateSubscription = async (companyId: number, subscriptionId: number, data: Partial<Subscription>): Promise<Subscription | null> => {
  try {
    const response = await axios.put(`/v2/companies/admin/${companyId}/subscriptions/${subscriptionId}`, data);
    toast.success("Subscription updated successfully");
    return response.data;
  } catch (error: any) {
    console.error("Error updating subscription:", error);
    toast.error(error?.response?.data?.message || "Failed to update subscription");
    return null;
  }
};

/**
 * Delete a subscription (Admin only)
 */
export const deleteSubscription = async (companyId: number, subscriptionId: number): Promise<boolean> => {
  try {
    await axios.delete(`/v2/companies/admin/${companyId}/subscriptions/${subscriptionId}`);
    toast.success("Subscription deleted successfully");
    return true;
  } catch (error: any) {
    console.error("Error deleting subscription:", error);
    toast.error(error?.response?.data?.message || "Failed to delete subscription");
    return false;
  }
};
